import React from 'react';
import { Star, Award, Trophy, ArrowUpRight, CheckCircle2 } from 'lucide-react';
import { Reveal } from '../components/Reveal';
import LeadForm from '../components/LeadForm';
import { useContent } from '../context/ContentContext';
import LeadersGrid from '../components/LeadersGrid';

const RED = '#D01919';
const GOLD = '#C9A24B';
const INK = '#0A0A0A';
const SILVER = 'linear-gradient(180deg, #ffffff 0%, #d9d9d9 45%, #9a9a9a 100%)';
const ICONS = [Star, Trophy, Award];

const D = {
  eyebrow: 'Annual Compendium · Revealed at ABC', subtitle: 'Most Visionary Infra & Energy Leaders',
  description: 'An annual compendium by Enersider recognising the 100 most influential energy & infrastructure leaders making an exceptional contribution to the nation — unveiled each year at the Annual Business Conclave.',
  cover: 'https://customer-assets-lqy194kg.emergentagent.net/job_enersider-revamp/artifacts/4lu4itle_ChatGPT%20Image%20Oct%201%2C%202026%2C%2005_04_59%20PM.png',
  highlights: ['100 Leaders', 'One Vision', 'A Stronger India'], pillars: [],
  formEyebrow: 'Nominations open', formTitle: 'Nominate yourself or a leader',
  formIntro: 'Put forward a changemaker — or make your own case — for a place in India’s The100. Tell us, in your words, why the entry deserves to be recognised. Every nomination is reviewed by the Enersider editorial board.',
  sectors: ['Oil', 'Gas', 'Solar', 'Wind', 'Green Hydrogen', 'Bio-Energy', 'Nuclear', 'Hydro', 'Thermal', 'Climate', 'Water×Energy', 'Power', 'BESS', 'EVs', 'Infra×Energy'],
};

const pickFrom = (src) => (k) => {
  const x = src[k];
  return x !== undefined && x !== null && x !== '' && !(Array.isArray(x) && !x.length) ? x : D[k];
};

export default function The100() {
  const { the100, the100Leaders } = useContent();
  const v = pickFrom(the100 || {});
  return (
    <div className="pb-24" style={{ background: INK }} data-testid="the100-page">
      <section className="section-pad pt-32 md:pt-40 pb-16">
        <div className="max-w-[1500px] mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="text-white">
              <p className="eyebrow mb-4" style={{ color: RED }} data-testid="the100-eyebrow">{v('eyebrow')}</p>
              <h1 className="font-display text-6xl md:text-8xl leading-[0.9]">The <span style={{ backgroundImage: SILVER, WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>1</span><span style={{ color: RED }}>00</span></h1>
              <p className="font-display text-2xl md:text-3xl mt-3 text-white/90" data-testid="the100-subtitle">{v('subtitle')}</p>
              <p className="text-white/65 text-lg mt-6 max-w-xl leading-relaxed" data-testid="the100-description">{v('description')}</p>
              <div className="flex flex-wrap gap-5 mt-8">
                {v('highlights').map((h, i) => {
                  const Icon = ICONS[i % 3];
                  return <span key={i} className="inline-flex items-center gap-2 text-white/80" data-testid={`the100-highlight-${i}`}><Icon size={18} style={{ color: GOLD }} /> {h}</span>;
                })}
              </div>
              <a href="#nominate" className="inline-flex items-center gap-2 mt-9 font-semibold px-6 py-3.5 rounded-full" style={{ background: RED, color: '#ffffff' }} data-testid="the100-nominate-cta">Nominate for The100 <ArrowUpRight size={18} /></a>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="relative max-w-md mx-auto">
              <div className="rounded-2xl overflow-hidden shadow-2xl ring-1" style={{ boxShadow: `0 0 0 1px #ffffff22, 0 30px 80px rgba(208,25,25,.18)` }}>
                <img src={v('cover')} alt="The 100 — Special Edition" className="w-full h-full object-cover" data-testid="the100-cover" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section-pad py-10">
        <div className="max-w-[1500px] mx-auto grid md:grid-cols-3 gap-6">
          {v('pillars').map((p, i) => (
            <Reveal key={i} delay={i * 80}>
              <div className="rounded-2xl p-7 h-full border" style={{ borderColor: '#2a2a2a', background: '#141414' }} data-testid={`the100-pillar-${i}`}>
                <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: `${RED}22` }}><CheckCircle2 size={20} style={{ color: RED }} /></div>
                <h3 className="font-display text-2xl mt-4 text-white">{p.value}</h3>
                <p className="text-white/60 text-sm mt-2">{p.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {the100Leaders.length > 0 && <LeadersGrid leaders={the100Leaders} />}

      <section id="nominate" className="section-pad py-16">
        <div className="max-w-[1500px] mx-auto grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <div className="text-white">
              <p className="eyebrow mb-3" style={{ color: RED }}>{v('formEyebrow')}</p>
              <h2 className="font-display text-4xl md:text-5xl">{v('formTitle')}</h2>
              <p className="text-white/65 mt-5 max-w-md leading-relaxed">{v('formIntro')}</p>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <LeadForm
              type="The100 Nomination"
              recipient="founder@enersider.com"
              accent={RED}
              heading="The100 Nomination"
              cta="Submit nomination"
              extraFields={[
                { k: 'nominating', l: 'You are nominating', options: ['Myself', 'Another leader'], req: true, full: true },
                { k: 'nominee', l: 'Nominee name (person / organisation)', full: true, req: true },
                { k: 'designation', l: 'Designation' },
                { k: 'sector', l: 'Sector', options: v('sectors'), req: true },
                { k: 'why', l: 'Why should this entry be recognised as India’s The100?', area: true, full: true, req: true },
              ]}
            />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
